import type { MarketplaceEntry } from "../types/index";
import { OfficialCheck } from "./OfficialBadge";
import styles from "./PluginListView.module.css";

const EXTENSION_TYPE_CLASS: Record<string, string> = {
  plugin: "typeBadgePlugin",
  skill: "typeBadgeSkill",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

interface PluginListViewProps {
  plugins: MarketplaceEntry[];
  onSelectPlugin: (plugin: MarketplaceEntry) => void;
}

export default function PluginListView({ plugins, onSelectPlugin }: PluginListViewProps) {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Last Updated</th>
          <th>Type</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        {plugins.map((plugin) => (
          // The row carries a mouse-only onClick for full-row clickability. The
          // keyboard/SR path goes through the native <button> below, whose click
          // (real or Enter/Space-synthesized) bubbles up to this same handler —
          // so onSelectPlugin fires exactly once and the row stays a plain table
          // row (no role, tabindex, or key handler of its own).
          <tr
            key={plugin.installName ?? `${plugin.source}--${plugin.orgOrOwner}--${plugin.repoName}`}
            className={styles.row}
            onClick={() => onSelectPlugin(plugin)}
          >
            <td className={styles.nameCell}>
              <div className={styles.pluginName}>
                <OfficialCheck plugin={plugin} size={15} />
                {/*
                  A real <button> is the actionable control (native keyboard
                  Enter/Space + focus for free). It has no onClick of its own —
                  activation bubbles to the row's handler — so mouse and keyboard
                  share one navigation path with no pointer-intercepting overlay
                  covering the row's text and badges.
                */}
                <button type="button" className={styles.nameButton}>
                  {plugin.pluginName}
                </button>
              </div>
              <div className={styles.repoName}>{plugin.orgOrOwner}/{plugin.repoName}</div>
            </td>
            <td className={styles.updatedCell}>
              {(plugin.lastUpdatedAt && formatDate(plugin.lastUpdatedAt)) || "—"}
            </td>
            <td>
              {plugin.extensionType && (
                <span className={`${styles.typeBadge} ${styles[EXTENSION_TYPE_CLASS[plugin.extensionType] ?? "typeBadgePlugin"]}`}>
                  {plugin.extensionType}
                </span>
              )}
            </td>
            <td className={styles.descriptionCell}>
              {plugin.description || "No description available"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
